/*
 * The environment banner's text, kept pure so bannerText.test.ts runs under Jest today (#43).
 * PV builds (pv-preview.yml) add the frontend pull request's number and title (#148); every other build
 * keeps the label-and-API text.
 */

export interface BannerEnv {
  // REACT_APP_ENV_LABEL: DEV, PV, or PR <backend PR> on a PV build paired with a backend pull request.
  label: string;
  apiUrl: string;
  // REACT_APP_PV_PR and REACT_APP_PV_TITLE: set by the PV workflow only, and empty for a develop build.
  pvPr?: string;
  pvTitle?: string;
}

export interface BannerText {
  // Printed in bold. On a narrow screen this is the first line, with the description.
  lead: string;
  // The PR title with its type prefix removed, shortened when it is long. Absent when the title adds nothing.
  description?: string;
  // The API clause. On a narrow screen this is the second line.
  api: string;
  // The full description, when the banner had to shorten it.
  tooltip?: string;
}

export const DESCRIPTION_MAX = 80;
const ELLIPSIS = '...';

// The Conventional Commits `type(scope): ` that CONTRIBUTING.md asks of commit subjects and PR titles, plus `!`.
const TYPE_PREFIX = /^[a-z]+(\([a-z0-9_-]+\))?!?:\s*/;
const BACKEND_PR_LABEL = /^PR ([1-9][0-9]{0,5})$/;

export function apiHost(apiUrl: string): string {
  try {
    return new URL(apiUrl).host;
  } catch {
    return apiUrl || 'REACT_APP_API_URL is not set';
  }
}

// `feat(tools): add a Tools tab` -> `add a Tools tab`. A title without the prefix is kept whole.
export function prDescription(title: string): string {
  return title.replace(/\s+/g, ' ').trim().replace(TYPE_PREFIX, '').trim();
}

// Keeps the result at DESCRIPTION_MAX, and ends a cut title with "..." so the cut is visible.
function shorten(text: string): string {
  if (text.length <= DESCRIPTION_MAX) return text;
  return `${text.slice(0, DESCRIPTION_MAX - ELLIPSIS.length).trimEnd()}${ELLIPSIS}`;
}

export function bannerText({ label, apiUrl, pvPr, pvTitle }: BannerEnv): BannerText {
  const host = apiHost(apiUrl);
  const api = `API: ${host}`;
  if (!pvPr || !/^[1-9][0-9]{0,5}$/.test(pvPr)) {
    return { lead: label, api };
  }
  const backendPr = BACKEND_PR_LABEL.exec(label);
  const backend = backendPr ? `Backend PR ${backendPr[1]} ${api}` : `Backend ${api}`;
  const description = prDescription(pvTitle ?? '');
  const short = shorten(description);
  return {
    lead: `PV ${pvPr}`,
    description: description ? short : undefined,
    api: `→ ${backend}`,
    tooltip: short === description ? undefined : description,
  };
}
